import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Phone, Mail, MessageCircle, MapPin, Clock, Copy, Check, ArrowRight, FileText, Globe2 } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { usePhones, regionKey, EMAIL, OFFICES } from "@/lib/contacts";

const TX = {
  en: {
    eyebrow: "Contact", title: "Talk to a VENKA consultant", intro: "Call, write or chat with us — we answer in Portuguese, English and French. Pick the number for where you are.",
    callUs: "Call us", forYou: "Recommended for you", call: "Call", copy: "Copy", copied: "Copied",
    angolaSub: "Luanda office · customers in Angola", intlSub: "Tallinn office · customers in Estonia, Europe and every other country",
    chat: "Chat with us", chatSub: "Sales or support, right here on the website. We also email you the reply.", openChat: "Open the chat",
    email: "Email", emailSales: "Prices, new applications and help with your case", emailSupport: "Help with an existing case",
    offices: "Our offices", luanda: "Luanda, Angola", tallinn: "Tallinn, Estonia", online: "Online",
    onlineSub: "Full service from anywhere in the world: online forms, customer area, video calls.",
    hours: "Opening hours", hoursText: "Monday – Friday, 8:00 – 18:00 (local time of each office)",
    start: "Ready to start?", startSub: "Fill in the online form in about 15 minutes — your consultant contacts you within one business day.",
    apply: "Apply online", guide: "Customer guide (PDF)",
  },
  pt: {
    eyebrow: "Contacto", title: "Fale com um consultor VENKA", intro: "Ligue, escreva ou converse connosco — respondemos em português, inglês e francês. Escolha o número de onde se encontra.",
    callUs: "Ligue-nos", forYou: "Recomendado para si", call: "Ligar", copy: "Copiar", copied: "Copiado",
    angolaSub: "Escritório de Luanda · clientes em Angola", intlSub: "Escritório de Tallinn · clientes na Estónia, na Europa e em todos os outros países",
    chat: "Converse connosco", chatSub: "Comercial ou apoio, aqui mesmo no site. Também lhe enviamos a resposta por email.", openChat: "Abrir o chat",
    email: "Email", emailSales: "Preços, novos pedidos e ajuda com o seu processo", emailSupport: "Ajuda com um processo em curso",
    offices: "Os nossos escritórios", luanda: "Luanda, Angola", tallinn: "Tallinn, Estónia", online: "Online",
    onlineSub: "Serviço completo a partir de qualquer país: formulários online, área de cliente, videochamadas.",
    hours: "Horário", hoursText: "Segunda a sexta, 8h00 – 18h00 (hora local de cada escritório)",
    start: "Pronto para começar?", startSub: "Preencha o formulário online em cerca de 15 minutos — o seu consultor contacta-o no prazo de um dia útil.",
    apply: "Candidatar-se online", guide: "Guia do cliente (PDF)",
  },
  fr: {
    eyebrow: "Contact", title: "Parlez à un conseiller VENKA", intro: "Appelez, écrivez ou discutez avec nous — nous répondons en portugais, anglais et français. Choisissez le numéro selon votre pays.",
    callUs: "Appelez-nous", forYou: "Recommandé pour vous", call: "Appeler", copy: "Copier", copied: "Copié",
    angolaSub: "Bureau de Luanda · clients en Angola", intlSub: "Bureau de Tallinn · clients en Estonie, en Europe et dans tous les autres pays",
    chat: "Discutez avec nous", chatSub: "Commercial ou assistance, directement sur le site. Nous vous envoyons aussi la réponse par email.", openChat: "Ouvrir le chat",
    email: "Email", emailSales: "Prix, nouvelles demandes et aide sur votre dossier", emailSupport: "Aide sur un dossier en cours",
    offices: "Nos bureaux", luanda: "Luanda, Angola", tallinn: "Tallinn, Estonie", online: "En ligne",
    onlineSub: "Service complet depuis n'importe quel pays : formulaires en ligne, espace client, appels vidéo.",
    hours: "Horaires", hoursText: "Du lundi au vendredi, 8h00 – 18h00 (heure locale de chaque bureau)",
    start: "Prêt à commencer ?", startSub: "Remplissez le formulaire en ligne en 15 minutes environ — votre conseiller vous contacte sous un jour ouvrable.",
    apply: "Postuler en ligne", guide: "Guide client (PDF)",
  },
};

const PhoneCard = ({ k, p, tx, lang, recommended }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    try { navigator.clipboard.writeText(p.display); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch (e) { /* ignore */ }
  };
  return (
    <div className={`relative rounded-3xl border p-6 md:p-7 ${recommended ? "border-venka-green-light/50 bg-venka-blue/10 shadow-xl shadow-venka-blue/10" : "border-white/10 bg-card"}`} data-testid={`contact-phone-${k}`}>
      {recommended && <span className="absolute -top-3 left-6 rounded-full bg-venka-green px-3 py-0.5 text-[11px] font-semibold text-white">{tx.forYou}</span>}
      <div className="flex items-center gap-3">
        <span className="grid h-11 w-11 place-items-center rounded-2xl bg-venka-blue/15 text-venka-green-light">{k === "angola" ? <Phone className="h-5 w-5" /> : <Globe2 className="h-5 w-5" />}</span>
        <div>
          <p className="text-sm font-semibold text-white">{p.label[lang] || p.label.en}</p>
          <p className="text-xs text-slate-400">{k === "angola" ? tx.angolaSub : tx.intlSub}</p>
        </div>
      </div>
      <a href={`tel:${p.tel}`} className="mt-5 block font-serif text-3xl md:text-[34px] tracking-tight text-white hover:text-venka-green-light transition-colors" data-testid={`contact-number-${k}`}>{p.display}</a>
      <div className="mt-5 flex flex-wrap gap-2">
        <Button asChild className="rounded-full brand-gradient text-white hover:opacity-90"><a href={`tel:${p.tel}`}><Phone className="mr-1.5 h-4 w-4" />{tx.call}</a></Button>
        <Button type="button" variant="outline" onClick={copy} className="rounded-full border-white/20 text-slate-100 hover:bg-white/5">
          {copied ? <Check className="mr-1.5 h-4 w-4 text-venka-green-light" /> : <Copy className="mr-1.5 h-4 w-4" />}{copied ? tx.copied : tx.copy}
        </Button>
      </div>
    </div>
  );
};

export const ContactPage = () => {
  const { i18n } = useTranslation();
  const lang = ["pt", "fr"].includes((i18n.language || "").slice(0, 2)) ? i18n.language.slice(0, 2) : "en";
  const tx = TX[lang];
  const PHONES = usePhones();
  const mine = regionKey();
  const order = mine === "angola" ? ["angola", "intl"] : ["intl", "angola"];
  const card = "rounded-3xl border border-white/10 bg-card p-6 md:p-7";

  return (
    <div className="min-h-screen bg-background text-foreground font-sans flex flex-col">
      <Navbar />
      <main className="flex-1" data-testid="contact-page">
        <section className="mx-auto max-w-7xl px-6 pt-14 pb-10">
          <p className="text-xs font-semibold tracking-[0.22em] uppercase text-venka-green-light mb-3">{tx.eyebrow}</p>
          <h1 className="font-serif text-4xl md:text-5xl leading-[1.05] text-brand-gradient" data-testid="contact-title">{tx.title}</h1>
          <p className="mt-4 max-w-2xl text-base text-slate-300 leading-relaxed">{tx.intro}</p>
        </section>

        <section className="mx-auto max-w-7xl px-6 pb-8">
          <h2 className="mb-5 text-sm font-semibold uppercase tracking-wider text-slate-400">{tx.callUs}</h2>
          <div className="grid gap-6 md:grid-cols-2">
            {order.map((k) => <PhoneCard key={k} k={k} p={PHONES[k]} tx={tx} lang={lang} recommended={k === mine} />)}
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-6 pb-8 grid gap-6 md:grid-cols-2">
          <div className={card} data-testid="contact-chat">
            <span className="grid h-11 w-11 place-items-center rounded-2xl bg-venka-green/15 text-venka-green-light"><MessageCircle className="h-5 w-5" /></span>
            <p className="mt-4 text-lg font-semibold text-white">{tx.chat}</p>
            <p className="mt-1 text-sm text-slate-400">{tx.chatSub}</p>
            <Button asChild className="mt-5 rounded-full brand-gradient text-white hover:opacity-90" data-testid="contact-open-chat">
              <Link to="/contact?chat=1">{tx.openChat} <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
            </Button>
          </div>
          <div className={card} data-testid="contact-email">
            <span className="grid h-11 w-11 place-items-center rounded-2xl bg-venka-blue/15 text-venka-green-light"><Mail className="h-5 w-5" /></span>
            <p className="mt-4 text-lg font-semibold text-white">{tx.email}</p>
            <div className="mt-3 space-y-3">
              {[[EMAIL, tx.emailSales]].map(([m, sub]) => (
                <a key={m} href={`mailto:${m}`} className="flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-background/40 px-4 py-3 hover:border-venka-green-light/50 transition-colors">
                  <span><span className="block text-sm font-medium text-white">{m}</span><span className="block text-xs text-slate-400">{sub}</span></span>
                  <ArrowRight className="h-4 w-4 text-slate-500" />
                </a>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-6 pb-8">
          <h2 className="mb-5 text-sm font-semibold uppercase tracking-wider text-slate-400">{tx.offices}</h2>
          <div className="grid gap-6 md:grid-cols-3">
            {[["luanda", tx.luanda, OFFICES.luanda, PHONES.angola], ["tallinn", tx.tallinn, OFFICES.tallinn, PHONES.intl], ["online", tx.online, tx.onlineSub, PHONES.intl]].map(([k, title, text, ph]) => (
              <div key={k} className={card} data-testid={`office-${k}`}>
                <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-venka-green-light">{k === "online" ? <Globe2 className="h-3.5 w-3.5" /> : <MapPin className="h-3.5 w-3.5" />}{title}</p>
                <p className="mt-2 text-sm text-slate-300">{text}</p>
                <a href={`tel:${ph.tel}`} className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-white hover:text-venka-green-light"><Phone className="h-3.5 w-3.5" />{ph.display}</a>
              </div>
            ))}
          </div>
          <p className="mt-5 flex items-center gap-2 text-sm text-slate-400"><Clock className="h-4 w-4 text-venka-green-light" /><span className="font-medium text-slate-200">{tx.hours}:</span> {tx.hoursText}</p>
        </section>

        <section className="mx-auto max-w-7xl px-6 pb-24">
          <div className="rounded-3xl border border-white/10 brand-gradient-soft p-8 md:p-12 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <h2 className="font-serif text-3xl text-white">{tx.start}</h2>
              <p className="mt-2 max-w-xl text-slate-300">{tx.startSub}</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button asChild size="lg" className="rounded-full brand-gradient text-white hover:opacity-90" data-testid="contact-apply"><Link to="/apply">{tx.apply} <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
              <Button asChild size="lg" variant="outline" className="rounded-full border-white/20 text-slate-100 hover:bg-white/5">
                <a href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/public/guide.pdf?lang=${lang}`} target="_blank" rel="noreferrer"><FileText className="mr-2 h-4 w-4" />{tx.guide}</a>
              </Button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ContactPage;
